export type AdminModule =
  | "all"
  | "dashboard"
  | "about"
  | "users"
  | "news"
  | "gallery"
  | "yuvagaaz"
  | "deanery"
  | "redon"
  | "youcat"
  | "surveys"
  | "dyc";

export type AdminRoleId =
  | "admin"
  | "about_manager"
  | "news_manager"
  | "media_news_manager"
  | "media_manager"
  | "yuvagaaz_manager"
  | "redon_manager"
  | "redon_admin"
  | "youcat_admin";

export type AdminProfile = {
  uid?: string;
  name?: string;
  email_id?: string;
  role?: string;
  role_id?: AdminRoleId;
  modules?: AdminModule[];
};

type RoleConfig = {
  label: string;
  role: string;
  role_id: AdminRoleId;
  modules: AdminModule[];
  retired?: boolean;
};

const ROLE_CONFIGS: RoleConfig[] = [
  {
    label: "Master Admin",
    role: "Master Admin",
    role_id: "admin",
    modules: ["all"],
  },
  {
    label: "News User",
    role: "News Manager",
    role_id: "news_manager",
    modules: ["news"],
  },
  {
    label: "Media Admin",
    role: "Media Admin",
    role_id: "media_news_manager",
    modules: ["news", "gallery", "yuvagaaz"],
  },
  {
    label: "REDON Admin",
    role: "REDON Admin",
    role_id: "redon_manager",
    modules: ["redon"],
  },
  {
    label: "REDON Master",
    role: "REDON Master",
    role_id: "redon_admin",
    modules: ["redon"],
  },
  {
    label: "YouCat Admin",
    role: "YouCat Admin",
    role_id: "youcat_admin",
    modules: ["youcat"],
  },
  {
    label: "Removed Role",
    role: "Removed Role",
    role_id: "about_manager",
    modules: [],
    retired: true,
  },
  {
    label: "Removed Role",
    role: "Removed Role",
    role_id: "media_manager",
    modules: [],
    retired: true,
  },
  {
    label: "Removed Role",
    role: "Removed Role",
    role_id: "yuvagaaz_manager",
    modules: [],
    retired: true,
  },
];

export const ROLE_OPTIONS = ROLE_CONFIGS.filter((role) => !role.retired);

function getRoleConfig(roleId?: AdminRoleId) {
  return ROLE_CONFIGS.find((item) => item.role_id === roleId);
}

export function getModulesForRole(roleId?: AdminRoleId): AdminModule[] {
  const role = getRoleConfig(roleId);
  return role?.modules || [];
}

function normalizeRoleId(
  roleId?: string,
  roleName?: string,
): AdminRoleId | undefined {
  if (
    roleId === "admin" ||
    roleId === "about_manager" ||
    roleId === "news_manager" ||
    roleId === "media_news_manager" ||
    roleId === "media_manager" ||
    roleId === "yuvagaaz_manager" ||
    roleId === "redon_manager" ||
    roleId === "redon_admin" ||
    roleId === "youcat_admin"
  ) {
    return roleId;
  }

  const normalizedRoleName = roleName?.trim().toLowerCase();

  if (!normalizedRoleName) {
    return undefined;
  }

  if (
    normalizedRoleName === "admin" ||
    normalizedRoleName === "main admin" ||
    normalizedRoleName === "super admin" ||
    normalizedRoleName === "master admin"
  ) {
    return "admin";
  }

  if (
    normalizedRoleName === "about us manager" ||
    normalizedRoleName === "about manager" ||
    normalizedRoleName === "about us user"
  ) {
    return "about_manager";
  }

  if (
    normalizedRoleName === "news manager" ||
    normalizedRoleName === "news user"
  ) {
    return "news_manager";
  }

  if (
    normalizedRoleName === "media and news manager" ||
    normalizedRoleName === "media and news user" ||
    normalizedRoleName === "media admin"
  ) {
    return "media_news_manager";
  }

  if (
    normalizedRoleName === "media manager" ||
    normalizedRoleName === "media user"
  ) {
    return "media_manager";
  }

  if (
    normalizedRoleName === "yuvagaaz manager" ||
    normalizedRoleName === "yuvagaaz user"
  ) {
    return "yuvagaaz_manager";
  }

  if (
    normalizedRoleName === "redon manager" ||
    normalizedRoleName === "redon user" ||
    normalizedRoleName === "redon admin"
  ) {
    return "redon_manager";
  }

  if (
    normalizedRoleName === "redon master" ||
    normalizedRoleName === "redon super admin"
  ) {
    return "redon_admin";
  }

  if (
    normalizedRoleName === "youcat admin" ||
    normalizedRoleName === "you cat admin"
  ) {
    return "youcat_admin";
  }

  return undefined;
}

export function getRoleLabel(adminProfile?: AdminProfile | null) {
  if (!adminProfile) {
    return "";
  }

  const roleId = normalizeRoleId(adminProfile.role_id, adminProfile.role);
  const normalizedRoleName = adminProfile.role?.trim().toLowerCase() || "";
  const isMainAdmin =
    roleId === "admin" ||
    normalizedRoleName === "admin" ||
    normalizedRoleName === "main admin" ||
    normalizedRoleName === "master admin" ||
    adminProfile.modules?.includes?.("all");

  if (isMainAdmin) {
    return "Master Admin";
  }

  const roleConfig = getRoleConfig(roleId);

  if (roleConfig?.retired) {
    return "Removed Role";
  }

  if (roleConfig) {
    return roleConfig.label;
  }

  return adminProfile.role || "";
}

export function getAllowedModules(adminProfile?: AdminProfile | null): AdminModule[] {
  if (!adminProfile) {
    return [];
  }

  const normalizedRoleId = normalizeRoleId(
    adminProfile.role_id,
    adminProfile.role,
  );
  const roleConfig = getRoleConfig(normalizedRoleId);

  if (roleConfig?.retired) {
    return [];
  }

  if (adminProfile.modules?.length) {
    return adminProfile.modules;
  }

  if (roleConfig?.modules?.length) {
    return roleConfig.modules;
  }

  // Backward compatibility for legacy admin records that existed
  // before role/module-based access control was added.
  return ["all"];
}

export function canAccessModule(
  allowedModules: AdminModule[],
  module?: AdminModule,
) {
  if (!module) {
    return true;
  }

  if (module === "yuvagaaz" && allowedModules.includes("gallery")) {
    return true;
  }

  return allowedModules.includes("all") || allowedModules.includes(module);
}

export function getDefaultAdminPath(allowedModules: AdminModule[]) {
  if (allowedModules.includes("all") || allowedModules.includes("dashboard")) {
    return "/admin/home";
  }

  if (allowedModules.includes("users")) {
    return "/admin/users";
  }

  if (allowedModules.includes("about")) {
    return "/admin/about";
  }

  if (allowedModules.includes("news")) {
    return "/admin/news";
  }

  if (allowedModules.includes("gallery")) {
    return "/admin/gallery";
  }

  if (allowedModules.includes("yuvagaaz")) {
    return "/admin/yuvagaaz";
  }

  if (allowedModules.includes("deanery")) {
    return "/admin/deanery-members";
  }

  if (allowedModules.includes("redon")) {
    return "/admin/redon";
  }

  if (allowedModules.includes("youcat")) {
    return "/admin/youcat";
  }

  if (allowedModules.includes("surveys")) {
    return "/admin/surveys";
  }

  return "/admin/login";
}
